import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit, Building, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = () => {
  const [configs, setConfigs] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    tipo: 'IVA',
    categoria: '',
    porcentaje: '',
    activo: true,
  });
  const [empresaFormData, setEmpresaFormData] = useState({
    nombre: '',
    rif: '',
    direccion: '',
    telefono: '',
    email: '',
    secuencia_iva: 1,
    secuencia_islr: 1,
  });

  useEffect(() => {
    fetchConfigs();
    fetchEmpresa();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API}/configuracion`);
      setConfigs(response.data);
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Error al cargar configuración');
    }
  };

  const fetchEmpresa = async () => {
    try {
      const response = await axios.get(`${API}/empresa`);
      setEmpresa(response.data);
      setEmpresaFormData({
        nombre: response.data.nombre,
        rif: response.data.rif,
        direccion: response.data.direccion,
        telefono: response.data.telefono,
        email: response.data.email,
        secuencia_iva: response.data.secuencia_iva || 1,
        secuencia_islr: response.data.secuencia_islr || 1,
      });
    } catch (error) {
      console.error('Error fetching empresa:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConfig) {
        await axios.put(`${API}/configuracion/${editingConfig.id}`, formData);
        toast.success('Configuración actualizada exitosamente');
      } else {
        await axios.post(`${API}/configuracion`, formData);
        toast.success('Configuración creada exitosamente');
      }
      fetchConfigs();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta configuración?')) return;
    try {
      await axios.delete(`${API}/configuracion/${id}`);
      toast.success('Configuración eliminada exitosamente');
      fetchConfigs();
    } catch (error) {
      console.error('Error deleting config:', error);
      toast.error('Error al eliminar configuración');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      tipo: config.tipo,
      categoria: config.categoria,
      porcentaje: config.porcentaje.toString(),
      activo: config.activo,
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingConfig(null);
    setFormData({ tipo: 'IVA', categoria: '', porcentaje: '', activo: true });
  };

  const handleEmpresaSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/empresa`, empresaFormData);
      toast.success('Datos de la empresa actualizados exitosamente');
      fetchEmpresa();
    } catch (error) {
      console.error('Error updating empresa:', error);
      toast.error('Error al actualizar datos de la empresa');
    }
  };

  const ivaConfigs = configs.filter(c => c.tipo === 'IVA');
  const islrConfigs = configs.filter(c => c.tipo === 'ISLR');

  return (
    <div className="p-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 font-heading">Configuración</h1>
        <p className="text-sm text-slate-600 mt-2">Gestionar empresa y porcentajes de retención</p>
      </div>

      <Tabs defaultValue="empresa" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="empresa" data-testid="tab-empresa">
            <Building size={16} className="mr-2" />
            Datos de la Empresa
          </TabsTrigger>
          <TabsTrigger value="retenciones" data-testid="tab-retenciones">
            Porcentajes de Retención
          </TabsTrigger>
        </TabsList>

        <TabsContent value="empresa">
          <Card className="bg-white border border-slate-200 shadow-sm rounded-lg p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 font-heading">Agente de Retención</h2>
            <p className="text-sm text-slate-600 mb-6">
              Esta información aparecerá en todos los comprobantes de retención generados.
            </p>
            <form onSubmit={handleEmpresaSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="empresa_nombre">Razón Social *</Label>
                  <Input
                    id="empresa_nombre"
                    data-testid="empresa-nombre-input"
                    value={empresaFormData.nombre}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, nombre: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="empresa_rif">RIF *</Label>
                  <Input
                    id="empresa_rif"
                    data-testid="empresa-rif-input"
                    value={empresaFormData.rif}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, rif: e.target.value })}
                    placeholder="J-12345678-9"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="empresa_direccion">Dirección *</Label>
                <Input
                  id="empresa_direccion"
                  data-testid="empresa-direccion-input"
                  value={empresaFormData.direccion}
                  onChange={(e) => setEmpresaFormData({ ...empresaFormData, direccion: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="empresa_telefono">Teléfono</Label>
                  <Input
                    id="empresa_telefono"
                    data-testid="empresa-telefono-input"
                    value={empresaFormData.telefono}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, telefono: e.target.value })}
                    placeholder="+58 212-1234567"
                  />
                </div>
                <div>
                  <Label htmlFor="empresa_email">Email</Label>
                  <Input
                    id="empresa_email"
                    type="email"
                    data-testid="empresa-email-input"
                    value={empresaFormData.email}
                    onChange={(e) => setEmpresaFormData({ ...empresaFormData, email: e.target.value })}
                  />
                </div>
              </div>
              
              <div className="border-t border-slate-200 pt-4 mt-4">
                <h3 className="text-md font-semibold text-slate-900 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm mr-2">Paso Importante</span>
                  Numeración de Comprobantes
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Configure el número desde donde comenzará la secuencia de cada tipo de retención.
                  El sistema incrementará automáticamente con cada nueva retención generada.
                </p>
                
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-bold text-blue-900">Comprobantes IVA</h4>
                        <p className="text-xs text-blue-700 mt-1">Retenciones de Impuesto al Valor Agregado</p>
                      </div>
                      <div className="bg-white rounded-lg px-4 py-2 shadow-md">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Próximo Número</p>
                        <div className="flex items-center space-x-1 mt-1">
                          <span className="font-mono text-sm font-bold text-slate-600 bg-slate-200 px-2 py-1 rounded">
                            {new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}
                          </span>
                          <span className="font-mono text-lg font-bold text-blue-700">
                            {String(empresaFormData.secuencia_iva).padStart(8, '0')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <Label htmlFor="secuencia_iva" className="text-sm font-semibold text-blue-900">
                      Editar Secuencial (8 dígitos)
                    </Label>
                    <div className="flex items-center space-x-3 mt-2">
                      <div className="flex-shrink-0 bg-slate-200 border-2 border-slate-400 rounded-lg px-4 py-3 font-mono text-lg font-bold text-slate-700 shadow-inner">
                        {new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}
                      </div>
                      <Input
                        id="secuencia_iva"
                        type="text"
                        maxLength="8"
                        data-testid="empresa-secuencia-iva-input"
                        value={String(empresaFormData.secuencia_iva).padStart(8, '0')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          const numValue = parseInt(value) || 0;
                          if (numValue <= 99999999) {
                            setEmpresaFormData({ ...empresaFormData, secuencia_iva: numValue });
                          }
                        }}
                        className="font-mono text-xl font-bold border-2 border-blue-400 focus:border-blue-600 rounded-lg"
                        placeholder="00000001"
                      />
                    </div>
                    <div className="mt-3 bg-white/70 rounded-lg p-3">
                      <p className="text-xs text-blue-800">
                        💡 <strong>Ejemplo:</strong> Si ingresas <span className="font-mono bg-blue-200 px-2 py-0.5 rounded">00000502</span>, 
                        el próximo comprobante IVA será <span className="font-mono bg-blue-200 px-2 py-0.5 rounded">{new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}00000502</span>
                      </p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-lg font-bold text-amber-900">Comprobantes ISLR</h4>
                        <p className="text-xs text-amber-700 mt-1">Retenciones de Impuesto Sobre la Renta</p>
                      </div>
                      <div className="bg-white rounded-lg px-4 py-2 shadow-md">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Próximo Número</p>
                        <div className="flex items-center space-x-1 mt-1">
                          <span className="font-mono text-sm font-bold text-slate-600 bg-slate-200 px-2 py-1 rounded">
                            {new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}
                          </span>
                          <span className="font-mono text-lg font-bold text-amber-700">
                            {String(empresaFormData.secuencia_islr).padStart(8, '0')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <Label htmlFor="secuencia_islr" className="text-sm font-semibold text-amber-900">
                      Editar Secuencial (8 dígitos)
                    </Label>
                    <div className="flex items-center space-x-3 mt-2">
                      <div className="flex-shrink-0 bg-slate-200 border-2 border-slate-400 rounded-lg px-4 py-3 font-mono text-lg font-bold text-slate-700 shadow-inner">
                        {new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}
                      </div>
                      <Input
                        id="secuencia_islr"
                        type="text"
                        maxLength="8"
                        data-testid="empresa-secuencia-islr-input"
                        value={String(empresaFormData.secuencia_islr).padStart(8, '0')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          const numValue = parseInt(value) || 0;
                          if (numValue <= 99999999) {
                            setEmpresaFormData({ ...empresaFormData, secuencia_islr: numValue });
                          }
                        }}
                        className="font-mono text-xl font-bold border-2 border-amber-400 focus:border-amber-600 rounded-lg"
                        placeholder="00000001"
                      />
                    </div>
                    <div className="mt-3 bg-white/70 rounded-lg p-3">
                      <p className="text-xs text-amber-800">
                        💡 <strong>Ejemplo:</strong> Si ingresas <span className="font-mono bg-amber-200 px-2 py-0.5 rounded">00000100</span>, 
                        el próximo comprobante ISLR será <span className="font-mono bg-amber-200 px-2 py-0.5 rounded">{new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}00000100</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Formato SENIAT (Providencia 0049)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-blue-800">
                    <div className="bg-white rounded p-3">
                      <span className="font-mono font-bold text-slate-700">AAAA</span>
                      <p className="mt-1">Año actual (automático)</p>
                    </div>
                    <div className="bg-white rounded p-3">
                      <span className="font-mono font-bold text-slate-700">MM</span>
                      <p className="mt-1">Mes actual (automático)</p>
                    </div>
                    <div className="bg-white rounded p-3">
                      <span className="font-mono font-bold text-blue-700">SSSSSSSS</span>
                      <p className="mt-1">Secuencial (editable arriba)</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  data-testid="empresa-submit-btn"
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </Card>
        </TabsContent>

        <TabsContent value="retenciones">
          <div className="flex justify-end mb-6">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="add-config-btn"
                  className="bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg rounded-md px-6 py-2 font-bold tracking-wide"
                  onClick={() => setEditingConfig(null)}
                >
                  <Plus size={20} className="mr-2" />
                  Nueva Configuración
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingConfig ? 'Modificar porcentaje de retención existente' : 'Agregar nuevo porcentaje de retención'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="tipo">Tipo de Retención</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                      required
                    >
                      <SelectTrigger data-testid="config-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="IVA">IVA</SelectItem>
                        <SelectItem value="ISLR">ISLR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="categoria">Categoría</Label>
                    <Input
                      id="categoria"
                      data-testid="config-categoria-input"
                      value={formData.categoria}
                      onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                      placeholder={formData.tipo === 'IVA' ? 'Ej: 75%, 100%' : 'Ej: Servicios Profesionales, Honorarios'}
                      required
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.tipo === 'IVA' 
                        ? 'Nombre descriptivo para el porcentaje (75% o 100%)'
                        : 'Tipo de servicio o actividad profesional'
                      }
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="porcentaje">Porcentaje (%)</Label>
                    <Input
                      id="porcentaje"
                      type="number"
                      step="0.01"
                      data-testid="config-porcentaje-input"
                      value={formData.porcentaje}
                      onChange={(e) => setFormData({ ...formData, porcentaje: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="activo"
                      data-testid="config-activo-switch"
                      checked={formData.activo}
                      onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
                    />
                    <Label htmlFor="activo">Activo</Label>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="config-cancel-btn">
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="config-submit-btn" className="bg-slate-900 text-white hover:bg-slate-800">
                      {editingConfig ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 font-heading">Retenciones de IVA</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ivaConfigs.length === 0 ? (
                  <Card className="col-span-3 bg-white border border-slate-200 shadow-sm rounded-lg p-6 text-center">
                    <p className="text-slate-500">No hay configuraciones de IVA</p>
                  </Card>
                ) : (
                  ivaConfigs.map((config) => (
                    <Card
                      key={config.id}
                      data-testid={`config-card-${config.id}`}
                      className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 font-heading">{config.categoria}</h3>
                          <p className="text-xs text-slate-500 mt-1">
                            {config.activo ? 'Activo' : 'Inactivo'}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            data-testid={`edit-config-${config.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(config)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            data-testid={`delete-config-${config.id}`}
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(config.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Porcentaje</p>
                        <p className="text-3xl font-bold text-blue-700 font-mono">{config.porcentaje}%</p>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 font-heading">Retenciones de ISLR</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {islrConfigs.length === 0 ? (
                  <Card className="col-span-3 bg-white border border-slate-200 shadow-sm rounded-lg p-6 text-center">
                    <p className="text-slate-500">No hay configuraciones de ISLR</p>
                  </Card>
                ) : (
                  islrConfigs.map((config) => (
                    <Card
                      key={config.id}
                      data-testid={`config-card-${config.id}`}
                      className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 font-heading">{config.categoria}</h3>
                          <p className="text-xs text-slate-500 mt-1">
                            {config.activo ? 'Activo' : 'Inactivo'}
                          </p>
                        </div>
                        <div className="flex space-x-1">
                          <Button
                            data-testid={`edit-config-${config.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(config)}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button
                            data-testid={`delete-config-${config.id}`}
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(config.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                        <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">Porcentaje</p>
                        <p className="text-3xl font-bold text-amber-700 font-mono">{config.porcentaje}%</p>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </div>

          <Card className="mt-8 bg-white border border-slate-200 shadow-sm rounded-lg p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-4 font-heading">Ejemplos de Configuración</h2>
            <div className="space-y-3 text-sm text-slate-600">
              <div>
                <strong className="text-slate-900">IVA:</strong>
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li>75% - Para contribuyentes especiales</li>
                  <li>100% - Para proveedores no domiciliados</li>
                </ul>
              </div>
              <div>
                <strong className="text-slate-900">ISLR:</strong>
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li>3% - Servicios Profesionales no mercantiles</li>
                  <li>2% - Servicios Técnicos</li>
                  <li>5% - Honorarios profesionales</li>
                  <li>1% - Comisiones</li>
                </ul>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;